import React from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  ChevronLeft,
  MessageSquare,
  FileText,
  Share2,
  Video,
  Monitor,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import CarteraPhInteractiveDemo from "@/components/cartera-ph/CarteraPhInteractiveDemo";
import { toast } from "sonner";

export default function CarteraPHDemoPage() {
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Demo Interactiva | MiA Cartera PH",
        text: "Vea cómo opera la recuperación de cartera con botón PSE en WhatsApp bajo Ley 675 y Ley 2300:",
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Enlace de la demo copiado al portapapeles.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col selection:bg-emerald-100 selection:text-emerald-900">
      <SEO
        title="Demo Interactiva & Simulador | MiA Cartera PH"
        description="Simulador interactivo del flujo de recuperación de cartera para conjuntos residenciales y copropiedades en Colombia. Notificaciones oficiales de WhatsApp con botón PSE y Título Ejecutivo Ley 675."
      />

      {/* Barra de navegación superior sobria */}
      <div className="border-b border-slate-200 bg-white/95 backdrop-blur-md sticky top-0 z-50 px-4 py-3">
        <div className="container mx-auto max-w-6xl flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 -ml-2 text-xs gap-1"
            >
              <Link to="/cartera-ph">
                <ChevronLeft className="h-4 w-4" />
                Volver a Cartera PH
              </Link>
            </Button>
            <div className="h-4 w-[1px] bg-slate-200 hidden sm:block" />
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-slate-900 tracking-tight">
                MiA Cartera PH
              </span>
              <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 text-[10px] hidden md:inline-flex">
                Simulador Oficial
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-xs gap-1.5 h-8 shadow-xs"
            >
              <Share2 className="h-3.5 w-3.5 text-slate-500" />
              Compartir
            </Button>

            <Button
              size="sm"
              asChild
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-medium text-xs gap-1.5 h-8 shadow-xs"
            >
              <a
                href="https://wa.me/573507679981?text=Hola%2C%20vi%20la%20demo%20interactiva%20de%20MiA%20Cartera%20PH%20y%20deseo%20una%20prueba%20para%20mi%20copropiedad."
                target="_blank"
                rel="noreferrer"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Solicitar Prueba
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <main className="flex-1 container mx-auto px-4 py-8 md:py-12 max-w-6xl space-y-10">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <Badge variant="outline" className="border-slate-300 text-slate-700 bg-white uppercase tracking-wider text-xs font-medium py-1 px-3 shadow-xs">
            Simulador de Cobranza LegalTech &bull; 4 Capítulos
          </Badge>
          <h1 className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            Así Funciona la Recuperación de Cartera en su Copropiedad
          </h1>
          <p className="text-slate-600 text-sm md:text-base leading-relaxed">
            Deje que la demo se reproduzca automáticamente o interactúe directamente: cargue el archivo de prueba, presione el botón PSE en el celular y descargue el Título Ejecutivo formal.
          </p>
        </div>

        {/* Reproductor / Demo Interactiva */}
        <CarteraPhInteractiveDemo />

        {/* Guía: Cómo grabar este video para enviarlo a Asambleas o Consejos */}
        <div className="p-6 rounded-2xl border border-slate-200 bg-white shadow-sm max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
              <Video className="h-4 w-4 text-emerald-700" />
              ¿Desea exportar un archivo de video .MP4 para enviar por WhatsApp?
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Puede grabar esta misma pantalla mientras se reproduce la demo:
            </p>
            <ul className="text-xs text-slate-600 space-y-1">
              <li>&bull; <strong>En Mac:</strong> Presione <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-800 border border-slate-200">Cmd + Shift + 5</code>, seleccione el reproductor y presione Grabar.</li>
              <li>&bull; <strong>Con Screen Studio / Loom:</strong> Inicie la grabación con un clic para capturar los movimientos del simulador.</li>
              <li>&bull; La locución explicativa viene incluida en los subtítulos inferiores de cada paso.</li>
            </ul>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              size="sm"
              variant="outline"
              asChild
              className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-xs gap-1.5 shadow-xs"
            >
              <Link to="/brochure-cartera-ph">
                <FileText className="h-3.5 w-3.5 text-slate-500" />
                Ver Brochure en PDF
              </Link>
            </Button>
            <Button
              size="sm"
              asChild
              className="bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs gap-1.5"
            >
              <a
                href="https://wa.me/573507679981?text=Hola%2C%20deseo%20agendar%20una%20demostraci%C3%B3n%20en%20vivo%20de%20MiA%20Cartera%20PH."
                target="_blank"
                rel="noreferrer"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Demostración en Vivo
              </a>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

